import React, { useEffect, useMemo, useState } from "react";
import {
  createCommission,
  updateCommission,
  getCommissionById,
} from "../../../services/commissionsServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";

export default function CommissionModal({
  isOpen,
  onClose,
  isEditMode,
  commissionId,
  onRefresh,
}) {
  const { addNotification } = useNotification();

  const [formData, setFormData] = useState({
    nom_commission: "",
    description_commission: "",
    photo_url: "",
  });

  const [errors, setErrors] = useState({});
  const [loadingCommission, setLoadingCommission] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const resetForm = () => {
      setFormData({
        nom_commission: "",
        description_commission: "",
        photo_url: "",
      });
      setErrors({});
      setLoadingCommission(false);
      setSaving(false);
    };

    if (isEditMode && commissionId) {
      const fetchCommission = async () => {
        try {
          setLoadingCommission(true);
          const data = await getCommissionById(commissionId);

          setFormData({
            nom_commission: data?.nom_commission || "",
            description_commission: data?.description_commission || "",
            photo_url: data?.photo_url || "",
          });
        } catch (err) {
          addNotification("Erreur lors du chargement de la commission.", "error");
          console.error("Erreur lors du chargement de la commission :", err);
        } finally {
          setLoadingCommission(false);
        }
      };

      fetchCommission();
    } else {
      resetForm();
    }
  }, [isOpen, isEditMode, commissionId, addNotification]);

  const previewUrl = useMemo(() => {
    return formData.photo_url?.trim() || "/img/no-image.jpg";
  }, [formData.photo_url]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const e = {};

    if (!formData.nom_commission.trim()) {
      e.nom_commission = "Le nom de la commission est requis.";
    }

    if (!formData.photo_url.trim()) {
      e.photo_url = "L’URL de l’image est requise.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    const payload = {
      nom_commission: formData.nom_commission.trim(),
      description_commission: formData.description_commission.trim(),
      photo_url: formData.photo_url.trim(),
    };

    try {
      setSaving(true);

      if (isEditMode) {
        await updateCommission(commissionId, payload);
        addNotification("Commission mise à jour avec succès.", "success");
      } else {
        await createCommission(payload);
        addNotification("Commission créée avec succès.", "success");
      }

      await onRefresh();
      onClose();
    } catch (err) {
      addNotification("Erreur lors de l'enregistrement de la commission.", "error");
      console.error("Erreur lors de l'enregistrement de la commission :", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="commission-modal-title"
      describedById="commission-modal-desc"
      initialFocusSelector="#commission-name"
    >
      <ModalHeader
        id="commission-modal-title"
        title={isEditMode ? "Modifier une commission" : "Ajouter une commission"}
        subtitle={
          isEditMode
            ? `Commission #${commissionId ?? ""}`
            : "Renseignez les informations de la commission"
        }
        onClose={onClose}
      />

      <ModalBody id="commission-modal-desc">
        {loadingCommission ? (
          <div className="py-8 text-center text-sm text-slate-500">
            Chargement de la commission…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-xs text-slate-600 dark:text-slate-300">
              Les champs marqués d’un <span className="text-pink-600">*</span>{" "}
              sont obligatoires.
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr),220px]">
              <div className="space-y-4">
                <Field label="Nom de la commission" required error={errors.nom_commission}>
                  <input
                    id="commission-name"
                    data-autofocus
                    type="text"
                    name="nom_commission"
                    value={formData.nom_commission}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass(errors.nom_commission)}
                    placeholder="Ex : Commission Arbitrage"
                  />
                </Field>

                <Field label="Description">
                  <textarea
                    name="description_commission"
                    value={formData.description_commission}
                    onChange={handleChange}
                    rows={5}
                    className={inputClass()}
                    placeholder="Description de la commission..."
                  />
                </Field>

                <Field label="URL de l’image" required error={errors.photo_url}>
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass(errors.photo_url)}
                    placeholder="https://..."
                  />
                </Field>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Aperçu visuel
                </div>

                <div className="mt-4 flex justify-center">
                  <img
                    src={previewUrl}
                    alt="Prévisualisation commission"
                    onError={(e) => {
                      e.currentTarget.src = "/img/no-image.jpg";
                    }}
                    className="h-28 w-28 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          text="Annuler"
          onClick={onClose}
          icon={FaTimes}
          type="button"
          variant="subtle"
          className="!rounded-xl"
        />
        <Button
          text={saving ? "Enregistrement…" : isEditMode ? "Enregistrer" : "Créer"}
          onClick={handleSubmit}
          icon={FaSave}
          type="submit"
          loading={saving}
          disabled={saving || loadingCommission}
          className="!rounded-xl"
        />
      </ModalFooter>
    </Modal>
  );
}

function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-pink-600">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
      {error ? <p className="mt-1 text-xs text-rose-500">{error}</p> : null}
    </div>
  );
}

function inputClass(error) {
  return `w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70 ${
    error
      ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
      : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
  }`;
}